"use client";

import type { QueryClientProviderProps } from "@tanstack/react-query";
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { createContext, useContext, useRef, useState } from "react";

import { isUserFacingRequestError } from "../../request/errors/requestError.error";
import { useSession } from "../../session/hooks/useSession";

declare module "@tanstack/react-query" {
  interface Register {
    mutationMeta: {
      suppressErrorToast?: boolean;
    };
  }
}

export type DataProviderProps = Omit<QueryClientProviderProps, "client">;

type UserFacingErrorHandler = (message: string) => void;

const setUserFacingErrorHandlerContext = createContext<
  (handler: UserFacingErrorHandler | null) => void
>(() => {});

// Lets a descendant register the MutationCache's toast handler without being an SdkProvider ancestor.
export const useSetUserFacingErrorHandler = () =>
  useContext(setUserFacingErrorHandlerContext);

export const DataProvider = ({ children, ...props }: DataProviderProps) => {
  const { refresh } = useSession();
  const handlerRef = useRef<UserFacingErrorHandler | null>(null);
  const setHandler = useRef((handler: UserFacingErrorHandler | null) => {
    handlerRef.current = handler;
  }).current;

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus:
              process.env.NODE_ENV === "development" ? false : true,
          },
        },
        /** Expected backend rejections are classified by isUserFacingRequestError and shown here by default, never reported to Bugsnag; opt out per-mutation with meta.suppressErrorToast once a screen renders the error itself. */
        mutationCache: new MutationCache({
          onError: async (
            error: any,
            variables,
            context: { noRefresh?: boolean },
            mutation,
          ) => {
            if (context?.noRefresh) return;
            if (error?.status === 401) {
              await refresh({ callback: () => mutation.execute(variables) });
              return;
            }
            if (
              isUserFacingRequestError(error) &&
              !mutation.meta?.suppressErrorToast
            ) {
              handlerRef.current?.(error.serverMessage);
            }
          },
        }),
        queryCache: new QueryCache({
          onError: async (error: any, query) => {
            if (error?.status === 401)
              await refresh({ callback: () => query.fetch() });
          },
        }),
      }),
  );

  return (
    <QueryClientProvider client={queryClient} {...props}>
      <setUserFacingErrorHandlerContext.Provider value={setHandler}>
        {children}
      </setUserFacingErrorHandlerContext.Provider>
    </QueryClientProvider>
  );
};
